import React from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { CommentFetchStatus } from '../../lib/types';
interface CommentsProgressProps {
  progress: CommentFetchStatus;
}
export function CommentsProgress({
  progress
}: CommentsProgressProps) {
  const getStatusColor = () => {
    switch (progress.status) {
      case 'in_progress':
        return 'text-blue-600 dark:text-blue-400';
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-muted-foreground';
    }
  };
  const getStatusIcon = () => {
    switch (progress.status) {
      case 'in_progress':
        return <RefreshCw size={16} className="animate-spin" data-id="element-1019" />;
      case 'completed':
        return <CheckCircle size={16} data-id="element-1020" />;
      case 'failed':
        return <AlertCircle size={16} data-id="element-1021" />;
      default:
        return null;
    }
  };
  return <div className="border border-primary/30 rounded-lg bg-card p-4" data-id="element-1022">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-1023">
        <h4 className="font-medium text-sm flex items-center gap-2" data-id="element-1024">
          <span className={getStatusColor()} data-id="element-1025">{getStatusIcon()}</span>
          {progress.status === 'in_progress' && 'Fetching Comments...'}
          {progress.status === 'completed' && 'Fetch Complete!'}
          {progress.status === 'failed' && 'Fetch Failed'}
        </h4>
        {progress.provider && <span className="text-xs text-muted-foreground" data-id="element-1026">
            via {progress.provider}
          </span>}
      </div>

      {/* Progress Bar */}
      <div className="mb-4" data-id="element-1027">
        <div className="flex items-center justify-between mb-2" data-id="element-1028">
          <span className="text-xs text-muted-foreground" data-id="element-1029">Progress</span>
          <span className="text-xs font-medium" data-id="element-1030">
            {progress.progress_percent.toFixed(1)}%
          </span>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden" data-id="element-1031">
          <div className={`h-full transition-all duration-500 ${progress.status === 'completed' ? 'bg-green-500' : progress.status === 'failed' ? 'bg-red-500' : 'bg-primary'}`} style={{
          width: `${progress.progress_percent}%`
        }} data-id="element-1032" />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-id="element-1033">
        <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1034">
          <div className="text-xs text-muted-foreground mb-1" data-id="element-1035">Fetched</div>
          <div className="text-lg font-semibold" data-id="element-1036">
            {progress.comments_fetched.toLocaleString()}
          </div>
        </div>
        <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1037">
          <div className="text-xs text-muted-foreground mb-1" data-id="element-1038">Estimated</div>
          <div className="text-lg font-semibold" data-id="element-1039">
            {progress.estimated_total.toLocaleString()}
          </div>
        </div>
        {progress.current_page && progress.total_pages_estimate && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1040">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1041">Page</div>
            <div className="text-lg font-semibold" data-id="element-1042">
              {progress.current_page}/{progress.total_pages_estimate}
            </div>
          </div>}
        {progress.eta_seconds && progress.status === 'in_progress' && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1043">
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1" data-id="element-1044">
              <Clock size={10} data-id="element-1045" />
              ETA
            </div>
            <div className="text-lg font-semibold" data-id="element-1046">{progress.eta_seconds}s</div>
          </div>}
        {progress.credits_charged !== undefined && <div className="bg-secondary/30 rounded-lg p-3" data-id="element-1047">
            <div className="text-xs text-muted-foreground mb-1" data-id="element-1048">Credits</div>
            <div className="text-lg font-semibold text-primary" data-id="element-1049">
              {progress.credits_charged}
            </div>
          </div>}
      </div>

      {/* Error Message */}
      {progress.status === 'failed' && progress.error_message && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-id="element-1050">
          <div className="flex items-start gap-2" data-id="element-1051">
            <AlertCircle size={16} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-1052" />
            <div className="text-xs text-destructive" data-id="element-1053">
              <p className="font-medium mb-1" data-id="element-1054">Error occurred:</p>
              <p data-id="element-1055">{progress.error_message}</p>
            </div>
          </div>
        </div>}

      {/* Success Message */}
      {progress.status === 'completed' && <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg" data-id="element-1056">
          <div className="flex items-start gap-2" data-id="element-1057">
            <CheckCircle size={16} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" data-id="element-1058" />
            <div className="text-xs text-green-600 dark:text-green-400" data-id="element-1059">
              <p className="font-medium" data-id="element-1060">
                Successfully fetched{' '}
                {progress.comments_fetched.toLocaleString()} comments!
              </p>
              <p className="text-xs mt-1" data-id="element-1061">
                Charged {progress.credits_charged}{' '}
                {progress.credits_charged === 1 ? 'credit' : 'credits'}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}